import { useEffect, useState } from 'react';
import { useAppDispatch } from 'app/hooks';
import {
  projectBoardApi,
  useGetShippingOrdersQuery,
  useGetBoardCompaniesQuery,
  useCreateShippingOrderMutation,
} from 'app/services/projectBoardApi';
import { getSocket, type SocketDataMessage } from 'app/socketClient';
import { useTabAccess, useBoardCurrentUser } from '../../hooks/useProjectBoardData';
import ShippingOrderCard from './ShippingOrderCard';

/**
 * SHIPPING TAB (`#shipping` — `shipping/view/shipping.js` + shipping.tmpl),
 * inventory §B6 / parity 182–192.
 *
 * Boot: the heading + "+ Order" + empty `ul#shipping-orders-list` render
 * immediately (the Backbone template renders before the fetch); cards render
 * once orders + company collection + current user resolve. The company
 * collection is the SHARED `'CompanyCollection'` session cache (one fetch per
 * session across General Info / Shipping / Service — parity 205) ⇒ the shared
 * RTK getBoardCompanies cache.
 *
 * `+ Order` POSTs `{project_id}` and PREPENDS an EXPANDED card with
 * order_title focused (parity 183); existing cards render collapsed.
 *
 * PERMISSION GATING (parity 191 / risk 8): the legacy composite disables its
 * own buttons pre-render (the + Order gating that works) but NEVER passes
 * `permission` to cards created through the add path — a freshly created
 * card bypasses read-only gating ([NEG]-quirk). Per the inventory's explicit
 * ruling ("fix in React + verify note") and the 3b working-read-only
 * precedent, ALL cards — including new ones — are gated by the tab access
 * here; the server gates writes anyway (Activities.Write +
 * project_board_tab.shipping).
 *
 * REALTIME (parity 192): socket `put` on django model `ksdmprojectshipping`
 * (models.mapping → 'model.project.shipping_order') merges `msg.data` into
 * the cached order row — the card re-renders from it. The legacy
 * `instance.set(msg.data)` would ALSO trip the model's autosave (an echo
 * PATCH of the just-received fields) — pure write amplification, not
 * replicated (3c/3d precedent).
 */

const SHIPPING_DJANGO_MODEL = 'ksdmprojectshipping';

const ShippingTab = ({ projectId }: { projectId: number }) => {
  const dispatch = useAppDispatch();
  const { data: orders } = useGetShippingOrdersQuery(projectId, {
    skip: !Number.isFinite(projectId),
  });
  const { data: companies } = useGetBoardCompaniesQuery();
  const { data: me } = useBoardCurrentUser();
  const access = useTabAccess(projectId, 'shipping');
  const [createOrder] = useCreateShippingOrderMutation();

  // Ids created in THIS session — those cards mount expanded with the title
  // focused (`checkboxState: true` + the init() order_title focus).
  const [newIds, setNewIds] = useState<ReadonlySet<number>>(() => new Set());

  useEffect(() => {
    if (!Number.isFinite(projectId)) return undefined;
    const socket = getSocket();
    const onData = (msg: SocketDataMessage) => {
      if (!msg || msg.model !== SHIPPING_DJANGO_MODEL || msg.code !== 'put') return;
      const data = (msg.data ?? {}) as Record<string, unknown>;
      const id = Number((data as { id?: unknown }).id ?? msg.id);
      if (!Number.isFinite(id)) return;
      dispatch(
        projectBoardApi.util.updateQueryData('getShippingOrders', projectId, (draft) => {
          const row = draft.find((r) => r.id === id);
          if (row) Object.assign(row, data);
        })
      );
    };
    socket.on('data', onData);
    return () => {
      socket.off('data', onData);
    };
  }, [projectId, dispatch]);

  const onAddOrder = () => {
    if (!access) return;
    createOrder({ projectId })
      .unwrap()
      .then((row) => {
        setNewIds((prev) => new Set(prev).add(row.id));
      })
      .catch(() => undefined); // failed POST: no card (legacy success-only)
  };

  const ready = !!(orders && companies && me);

  return (
    <div className="content">
      <div className="content-item col-xs-12">
        <div className="col-xs-12">
          <div className="col-xs-12" style={{ height: '100%' }}>
            <div className="group-heading main-title clearfix">
              <h2 className="left">Shipping Orders</h2>
              <button
                id="add-order"
                className={`right btn-add${access ? '' : ' non-clickable'}`}
                tabIndex={-1}
                style={{ margin: 0, marginLeft: 'auto', position: 'unset', right: 0 }}
                onClick={onAddOrder}
              >
                <i
                  className="plus"
                  style={{ fontSize: 17, paddingRight: 9, position: 'relative', top: 1 }}
                >
                  +
                </i>
                Order
              </button>
            </div>
            <ul
              id="shipping-orders-list"
              className="shipping-orders-list prj-collapsible-list col-xs-12"
            >
              {ready &&
                orders!.map((order) => (
                  <ShippingOrderCard
                    key={order.id}
                    projectId={projectId}
                    order={order}
                    companies={companies!}
                    me={me!}
                    access={access}
                    // Live baseline: tmpl hardcodes `collapse in` — every card
                    // renders EXPANDED on load (3e verify corrected parity 183).
                    initiallyExpanded
                    autoFocusTitle={newIds.has(order.id)}
                  />
                ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShippingTab;
